import { useEmailPreviewFrame } from "@/lib/utils/useEmailPreviewFrame";
import { useTranslations } from "next-intl";
import { trackingModeNotice } from "./trackingModeNotice";
import { Card } from "../Card/Card";
import { useEffect, useState, useRef, useMemo } from "react";
import { useSentMailsDetails } from "@/providers/SentMailsDetailsProvider";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { PError } from "../PError/PError";
import { EmailSentDTOStatistics } from "@/types/EmailSentDTOStatistics";
import { AnimatePresence, motion } from "framer-motion";
import { IconX } from "@tabler/icons-react";
import { createPortal } from "react-dom";
import {
  EmailDeliveryEventCard,
  type RecipientRoles,
} from "./components/EmailDeliveryEventCard";
import { AttachmentDropsBlock } from "./components/AttachmentDropsBlock";
import { AttachmentIncompleteBlock } from "./components/AttachmentIncompleteBlock";
import { SkipReasonBlock } from "./components/SkipReasonBlock";
import { FailReasonBlock } from "./components/FailReasonBlock";
import { EmailStatus } from "@/types/enums/EmailStatus";
import { formatTimestamp } from "./components/EmailDeliveryEventCard";
import { neutralizeDataTableMobileStack } from "@/lib/utils/expand-data-table-markers";
import { isOnBehalfFrom, mailboxAddress } from "@/lib/utils/sender-display";
import { OnBehalfChip } from "../SenderOnBehalf/OnBehalfChip";
import { isStaffNotice } from "@/lib/email/sendClass";
import { useOptionalRole } from "@/providers/RoleProvider";

interface PEmailSentProps {
  onClose: () => void;
  emailSentId: string;
}


function writeSentHtml(doc: Document, html: string) {
  // document.write, not innerHTML on the root, so a stored builder
  // email keeps its doctype (standards mode) and the frame can shrink again
  // — see Preview.tsx writePreviewHtml (W1-3).
  doc.open();
  doc.write(neutralizeDataTableMobileStack(html));
  doc.close();
}

export const PEmailSent: React.FC<PEmailSentProps> = ({
  onClose,
  emailSentId,
}) => {
  const { loading, error, getSentMailDetails, setError } =
    useSentMailsDetails();
  const [emailSentContent, setEmailSentContent] =
    useState<EmailSentDTOStatistics | null>(null);
  const [mounted, setMounted] = useState(false);
  const t = useTranslations("pEmailSent");
  // The guest drawer already words a missing subject as "(No subject)" for
  // the same row (SentMailEntry); a bare dash here was a second, untranslated
  // answer to the same question — and one screen readers skip outright.
  const tGuest = useTranslations("guestDetails");
  const trackingNotice = trackingModeNotice(emailSentContent?.emailSent);
  // W4-3 / 4e: a STAFF_NOTICE row is the arrival mail to a staff CONTACT, not
  // a mail to the guest. The "To" line names the kind of recipient; the
  // address itself is shown only to a role that may manage the contacts
  // (OWNER/ADMIN — the same gate as the notifications settings page). The
  // backend blanks `recipientMail` for every other role, and this component is
  // also mounted without a RoleProvider, so a missing role reads as "hide".
  const staffNotice = isStaffNotice(emailSentContent?.emailSent?.sendClass);
  const roleContext = useOptionalRole();
  const mayShowContactAddress =
    roleContext !== null && (roleContext.role === "OWNER" || roleContext.role === "ADMIN");
  // Sender on behalf: the stored From is what really went out, so a rewritten
  // one is recognised by its domain — no need to know how the sender row
  // looked at send time.
  const sentOnBehalf = isOnBehalfFrom(emailSentContent?.emailSent?.senderMail);
  // The stored Reply-To is where the recipient's answer went
  // (`replyToMail` is in SENT_MAIL_DETAIL_FIELDS), so it sits right under the
  // From whenever it differs from it — on behalf, but just as much for the
  // cap fallback (From `noreply@veltric.app`, Reply-To the address) or a
  // template's own Reply-To (audit r1 L7-5). Not tied to the build flag: it
  // is a fact of the stored mail.
  const sentReplyToRaw = emailSentContent?.emailSent?.replyToMail?.trim() ?? "";
  const sentReplyTo =
    sentReplyToRaw !== "" &&
    mailboxAddress(sentReplyToRaw) !== mailboxAddress(emailSentContent?.emailSent?.senderMail)
      ? sentReplyToRaw
      : "";

  // Which address was addressed how, so the timeline can say "bounced:
  // kollege@… (Kopie)" instead of leaving the reader to guess whether the
  // guest's own mail failed.
  const recipientRoles = useMemo<RecipientRoles>(
    () => ({
      to: emailSentContent?.emailSent?.recipientMail,
      cc: emailSentContent?.emailSent?.ccMail,
      bcc: emailSentContent?.emailSent?.bccMail,
      labels: { to: t("roleTo"), cc: t("roleCc"), bcc: t("roleBcc") },
    }),
    [emailSentContent, t],
  );

  // Desktop-width frame (>= 640px, above the mail's own mobile breakpoint —
  // 620 for a 600 px mail, its width + 20 for a wider one;
  // grows once per email to its own width) — useEmailPreviewFrame. The
  // `.datatable-block` neutraliser stays as belt and braces so the audit view
  // shows the desktop table the recipient saw on desktop.
  const {
    containerRef,
    iframeRef,
    width: iframeWidth,
    height: iframeHeight,
    scale,
  } = useEmailPreviewFrame(emailSentContent?.htmlContent || null, writeSentHtml);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async () => {
      try {
        const content = await getSentMailDetails(emailSentId);
        setEmailSentContent(content);
      } catch (err) {
        console.error("Failed to fetch email content:", err);
      }
    };

    fetchData();
  }, [emailSentId, getSentMailDetails]);

  if (!mounted) return null;

  return createPortal(
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 sm:p-8 z-[60] sm:pr-[calc(2rem+var(--ai-rail-push))] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="w-full max-w-[1080px] h-full flex flex-col min-h-0"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ type: "spring", damping: 20, stiffness: 300 }}
      >
        <Card
          padding="p-6"
          surfaceColor="surface-card"
          width="w-full"
          height="h-full"
          className="z-50 overflow-hidden"
          justify="gap-6"
          header={t("mailPreview")}
          icon={IconX}
          iconActionOnClick={onClose}
        >
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 flex-1 min-h-0">
              <div className="flex flex-col min-h-0">
                {/* FIRST for a withheld send: the boxes below are legitimately
                    empty for it (no subject was ever rendered), and this is
                    what turns "empty" into "explained". */}
                <SkipReasonBlock
                  emailStatus={emailSentContent?.emailSent?.emailStatus}
                  skipReason={emailSentContent?.emailSent?.skipReason}
                />
                <FailReasonBlock
                  emailStatus={emailSentContent?.emailSent?.emailStatus}
                  failReason={emailSentContent?.emailSent?.failReason}
                />
                <div className="flex flex-col mb-4 surface-tertiary px-4 py-3 rounded-lg">
                  <div className="flex flex-row body-2-medium">
                    {t("template")}:{" "}
                    {emailSentContent?.emailSent?.templateName ?? ""}
                  </div>
                </div>
                <div className="flex flex-col mb-8 surface-tertiary px-4 py-3 rounded-lg">
                  <div className="flex flex-row body-2-medium">
                    {t("subject")}:{" "}
                    {emailSentContent?.emailSent?.mailSubject ||
                      tGuest("noSubject")}
                  </div>
                  <div className="flex flex-row flex-wrap items-center gap-x-2 min-w-0 break-all body-2-medium">
                    <span>
                      {t("from")}: {emailSentContent?.emailSent?.senderMail}
                    </span>
                    {sentOnBehalf && <OnBehalfChip />}
                  </div>
                  {sentReplyTo !== "" && (
                    <div
                      className="flex flex-row min-w-0 break-all body-2-medium"
                      data-testid="sent-mail-reply-to"
                    >
                      {t("replyTo")}: {sentReplyTo}
                    </div>
                  )}
                  {staffNotice ? (
                    <div
                      className="flex flex-row flex-wrap gap-x-2 min-w-0 break-all body-2-medium"
                      data-testid="sent-mail-to-notification-contact"
                    >
                      <span>
                        {t("to")}: {t("toNotificationContact")}
                        {/* Lens 6 #15: name the contact when the row says
                            which one — EDITOR/VIEWER see no address, so the
                            label is all they get. */}
                        {emailSentContent?.emailSent?.notificationContactLabel ? (
                          <span data-testid="sent-mail-contact-label">
                            {" "}({emailSentContent.emailSent.notificationContactLabel})
                          </span>
                        ) : null}
                      </span>
                      {mayShowContactAddress && emailSentContent?.emailSent?.recipientMail ? (
                        <span data-testid="sent-mail-contact-address">
                          ({emailSentContent.emailSent.recipientMail})
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <div className="flex flex-row body-2-medium">
                      {t("to")}: {emailSentContent?.emailSent?.recipientMail}
                    </div>
                  )}
                  {/* Loaded all along, never shown. Without them the timeline
                      below names recipients the reader has no way to place. */}
                  {emailSentContent?.emailSent?.ccMail && (
                    <div className="flex flex-row body-2-medium">
                      {t("cc")}: {emailSentContent.emailSent.ccMail}
                    </div>
                  )}
                  {emailSentContent?.emailSent?.bccMail && (
                    <div className="flex flex-row body-2-medium">
                      {t("bcc")}: {emailSentContent.emailSent.bccMail}
                    </div>
                  )}
                  {/* W4-e Tier B: the one per-mail "acted on it" fact. Shown
                      only when portal-backend stamped it — an absent stamp is
                      "no visit recorded", not a statement about the guest. */}
                  {emailSentContent?.emailSent?.portalVisitedViaMailAt && (
                    <div
                      className="flex flex-row body-2-medium"
                      data-testid="sent-mail-portal-visited"
                    >
                      {t("portalVisitedViaMail")}:{" "}
                      {formatTimestamp(emailSentContent.emailSent.portalVisitedViaMailAt)}
                    </div>
                  )}
                  {/* W4-e ⚑I: the website twin — reached the event website
                      through this mail's link. Same rule: absent = nothing. */}
                  {emailSentContent?.emailSent?.websiteVisitedViaMailAt && (
                    <div
                      className="flex flex-row body-2-medium"
                      data-testid="sent-mail-website-visited"
                    >
                      {t("websiteVisitedViaMail")}:{" "}
                      {formatTimestamp(emailSentContent.emailSent.websiteVisitedViaMailAt)}
                    </div>
                  )}
                </div>
                <AttachmentDropsBlock
                  drops={emailSentContent?.emailSent?.attachmentDrops}
                />
                <AttachmentIncompleteBlock
                  degraded={emailSentContent?.emailSent?.attachmentDegraded}
                />
                <div
                  ref={containerRef}
                  className="overflow-y-auto overflow-x-hidden flex-1 min-h-0"
                >
                  <div
                    style={{
                      height: `${(iframeHeight || 800) * scale}px`,
                    }}
                  >
                    <iframe
                      ref={iframeRef}
                      title={t("emailPreview")}
                      sandbox="allow-same-origin"
                      style={{
                        width: `${iframeWidth}px`,
                        height: `${iframeHeight || 800}px`,
                        border: "none",
                        transformOrigin: "top left",
                        transform: `scale(${scale})`,
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col min-h-0 overflow-y-auto border outline-quaternary rounded-xl">
                {/* ABOVE the list, never instead of it: a mail sent without an
                    open pixel still has SEND / DELIVERY / CLICK events worth
                    showing. What the notice explains is the OPEN that will
                    never appear among them. */}
                {trackingNotice !== null && (
                  <p
                    className="px-4 py-3 body-3-regular font-tertiary border-b outline-quaternary"
                    data-testid="tracking-mode-notice"
                  >
                    {t(trackingNotice)}
                  </p>
                )}
                {emailSentContent?.emailDeliveryEvents &&
                emailSentContent.emailDeliveryEvents.length > 0 ? (
                  emailSentContent.emailDeliveryEvents.map((event, index) => (
                    <EmailDeliveryEventCard
                      key={index}
                      event={event}
                      t={t}
                      roles={recipientRoles}
                    />
                  ))
                ) : (
                  <div className="flex items-center justify-center p-6">
                    <p className="body-2-regular font-tertiary">
                      {/* A FAILED row has no SES message id, so this is not a
                          gap in the timeline — say so (board H-14). */}
                      {emailSentContent?.emailSent?.emailStatus === EmailStatus.FAILED
                        ? t("noEventsFailed")
                        : t("noEvents")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          <AnimatePresence>
            {error && (
              <PError messages={[error]} onClose={() => setError(null)} />
            )}
          </AnimatePresence>
        </Card>
      </motion.div>
    </motion.div>,
    document.body,
  );
};
